import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { getImage } from '../../graphql/queries';
import { deleteImageAdmin, updateImageStatus, updateFeedStatus } from '../../graphql/mutation';
import {
  Card,
  Descriptions,
  Tag,
  Spin,
  Row,
  Col,
  Button,
  Select,
  Alert,
  Space,
  Divider,
  Typography,
  Modal,
  message
} from 'antd';
import {
  DeleteOutlined,
  SaveOutlined,
  PlayCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  StopOutlined
} from '@ant-design/icons';
import './ImageDetail.css';

const { Option } = Select;
const { Title, Text } = Typography;

const feedStatusOptions = [
  { value: 'PENDING', label: 'Pending', color: 'purple' },
  { value: 'CONFIRMED', label: 'Confirmed', color: 'green' },
  { value: 'REJECTED', label: 'Rejected', color: 'red' },
];

const statusOptions = [
  { value: 'RUNNING', label: 'Running', icon: <PlayCircleOutlined />, color: 'blue' },
  { value: 'SUCCEEDED', label: 'Succeeded', icon: <CheckCircleOutlined />, color: 'green' },
  { value: 'FAILED', label: 'Failed', icon: <CloseCircleOutlined />, color: 'red' },
  { value: 'IN_QUE', label: 'In Queue', icon: <ClockCircleOutlined />, color: 'orange' },
  { value: 'PENDING', label: 'Pending', icon: <ClockCircleOutlined />, color: 'purple' },
  { value: 'THROTTLED', label: 'Throttled', icon: <WarningOutlined />, color: 'gold' },
  { value: 'MODERATION', label: 'Moderation', icon: <StopOutlined />, color: 'magenta' }
];

const ImageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [imageStatus, setImageStatus] = useState('');
  const [feedStatus, setFeedStatus] = useState('');

  const { loading, error, data, refetch } = useQuery(getImage, {
    variables: { imageId: id },
  });

  const [updateImageStatusMutation] = useMutation(updateImageStatus);
  const [updateFeedMutation] = useMutation(updateFeedStatus);
  const [deleteImageMutation] = useMutation(deleteImageAdmin);

  useEffect(() => {
    if (data?.getImage) {
      setImageStatus(data.getImage.status);
      setFeedStatus(data.getImage.feed?.status || '');
    }
  }, [data]);

  const handleUpdateFeedStatus = async () => {
    try {
      await updateFeedMutation({
        variables: { feedId: data.getImage.feed.id, status: feedStatus }
      });
      message.success('Feed status updated successfully');
      refetch();
    } catch (err) {
      message.error('Failed to update feed status');
    }
  };

  const handleUpdateImageStatus = async () => {
    try {
      await updateImageStatusMutation({
        variables: { imageId: id, status: imageStatus }
      });
      message.success('Image status updated successfully');
      refetch();
    } catch (err) {
      message.error('Failed to update image status');
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Are you sure you want to delete this image?',
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteImageMutation({ variables: { imageId: id } });
          message.success('Image deleted successfully');
          navigate('/images');
        } catch (err) {
          message.error('Failed to delete image');
        }
      },
    });
  };

  if (loading) return <Spin size="large" className="image-detail-loading" />;
  if (error) return <Alert type="error" message={error.message} />;

  const image = data.getImage;

  return (
    <div className="image-detail-container">
      <Row gutter={[24, 24]}>
        <Col span={12}>
          <Card className="info-card">
            <Title level={4}>Image Information</Title>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="ID">{image.id}</Descriptions.Item>
              <Descriptions.Item label="Job ID">
                {image.jobId ? (
                  <a
                    href={`${process.env.REACT_APP_QUEUE_SERVICE_DASHBOARD_URL}/images/by-job/${image.jobId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {image.jobId}
                  </a>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="User ID">
                <Button
                  type="link"
                  onClick={() => navigate(`/user/${image.userId}`)}
                  style={{ padding: 0 }}
                >
                  {image.userId}
                </Button>
              </Descriptions.Item>
              <Descriptions.Item label="Prompt">{image.prompt || '-'}</Descriptions.Item>
              <Descriptions.Item label="Model">
                <Tag color="blue">{image.model}</Tag>
              </Descriptions.Item>
              {image.modelName && (
                <Descriptions.Item label="Model Name">
                  <Tag color="cyan">{image.modelName}</Tag>
                </Descriptions.Item>
              )}
              {image.toolName && (
                <Descriptions.Item label="Tool">
                  <Tag
                    color="volcano"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/tools/${image.toolId}`)}
                  >
                    {image.toolName}
                  </Tag>
                </Descriptions.Item>
              )}
              {image.type && (
                <Descriptions.Item label="Type">
                  <Tag color="geekblue">{image.type}</Tag>
                </Descriptions.Item>
              )}
              {(image.ratioKey || image.aspectRatio) && (
                <Descriptions.Item label="Aspect Ratio">
                  <Tag color="blue">{image.ratioKey || image.aspectRatio}</Tag>
                </Descriptions.Item>
              )}
              {image.resolution && (
                <Descriptions.Item label="Resolution">
                  <Tag color="blue">{image.resolution}</Tag>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Credit Cost">
                {image.creditCost != null ? image.creditCost : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Actual Cost">
                {image.actualCost != null ? `$${image.actualCost.toFixed(4)}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {new Date(image.createdAt).toLocaleString('tr-TR', {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {new Date(image.updatedAt).toLocaleString('tr-TR', {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusOptions.find(s => s.value === image.status)?.color}>
                  {statusOptions.find(s => s.value === image.status)?.icon} {image.status}
                </Tag>
              </Descriptions.Item>
              {image.errorReason && (
                <Descriptions.Item label="Error">
                  <Text type="danger">{image.errorReason}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Title level={5}>Update Image Status</Title>
                <Space>
                  <Select
                    value={imageStatus}
                    onChange={setImageStatus}
                    style={{ width: 200 }}
                  >
                    {statusOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        <Space>
                          {option.icon}
                          {option.label}
                        </Space>
                      </Option>
                    ))}
                  </Select>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleUpdateImageStatus}
                  >
                    Update Status
                  </Button>
                </Space>
              </div>

              {image.feed && (
                <div>
                  <Title level={5}>Update Feed Status</Title>
                  <Space>
                    <Select
                      value={feedStatus}
                      onChange={setFeedStatus}
                      style={{ width: 200 }}
                    >
                      {feedStatusOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          <Tag color={option.color}>{option.label}</Tag>
                        </Option>
                      ))}
                    </Select>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleUpdateFeedStatus}
                    >
                      Update Feed
                    </Button>
                  </Space>
                </div>
              )}

              <Button
                danger
                type="primary"
                icon={<DeleteOutlined />}
                onClick={handleDelete}
                block
              >
                Delete Image
              </Button>
            </Space>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Image Preview" className="preview-card">
            <div className="image-container">
              {image.url ? (
                <div className="image-wrapper">
                  <img
                    src={image.url}
                    alt="Generated"
                    className="image-preview"
                  />
                </div>
              ) : (
                <div className="no-image">No image available</div>
              )}
            </div>

            {image.inputImageUrl && (
              <div className="input-image-section">
                <h3>Input Image</h3>
                <div className="input-image-wrapper">
                  <img
                    src={image.inputImageUrl}
                    alt="Input"
                    className="input-image"
                  />
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ImageDetail;
